import { queriesDocumentTemplates } from "@query";
import {
  DocumentTemplate,
  DocumentTemplatesApiCreateDocumentTemplateRequest
} from "@wingspanhq/wingspan-api-sdk";
import { useWSMutation } from "@ws-react-query";

import {
  helloSignClient,
  helloSignPromise
} from "../../../../../utils/helloSign";

/**
 * Mints the document template a `Signature` requirement definition points at,
 * then hands the payer the provider's embedded field editor to place the
 * signature and merge fields on it. Composed by hand rather than taken straight
 * from the registry because the editor round-trip is part of the operation: the
 * template is only usable once its fields are placed.
 *
 * TODO(v3-blocker): this flow currently fails against the backend on two counts,
 * both being worked on by the backend team:
 *   1. `fileId` rejects the vault-file id uploaded above with 422
 *      `ValidationError` `{ field: "fileId", code: "InvalidFormat" }` — it
 *      validates `format: wingspan-id` (22 chars) while vault-files mints a
 *      24-char mongo-style id.
 *   2. Every provider-backed endpoint on this surface answers 503 on
 *      integration (create past validation, and both template reads) while
 *      non-provider v3 reads answer 200 — 503 is the documented response when
 *      the e-signature provider is unavailable, so this may be environment
 *      config rather than a code defect.
 * Reproduced in `src/v3/api/tests/specs/documentTemplates.itest.ts`. The failure
 * surfaces at the upload step in the form, which renders the mutation error.
 */
export const useMutationCreateSignatureRequirementDefinitionDataSource = () => {
  const [createDocumentTemplate] =
    queriesDocumentTemplates.createDocumentTemplate.useMutation();

  return useWSMutation(
    async (
      request: DocumentTemplatesApiCreateDocumentTemplateRequest
    ): Promise<DocumentTemplate> => {
      const template = await createDocumentTemplate(request, {
        throwOnError: true
      });

      if (!template?.editUrl) {
        throw new Error("Document template was created without an edit url");
      }

      // open the provider's embedded field editor
      helloSignClient.open(template.editUrl);

      const status = await helloSignPromise();

      if (status === "closed" && template.id) {
        // open confirmation modal to create requirement

        return template;
      }

      throw Error("Something went wrong!");
    }
  );
};
